/**
 * Enhanced search utility that supports multiple search terms
 * 
 * @param searchTerm - The search string (can contain multiple space-separated terms)
 * @param fields - The fields to search in
 * @returns true if all search terms are found in any of the fields
 * 
 * Example:
 * matchesSearchTerms("Max Mustermann", customer.name, customer.email)
 * Will find results where both "Max" AND "Mustermann" appear anywhere in name or email
 */
/**
 * Normalize text by removing spaces and separators for flexible matching
 * Also removes invisible characters and trims
 */
const normalizeText = (text: string): string => {
  return text
    .trim() // remove leading/trailing whitespace
    .toLowerCase()
    .normalize('NFD') // split accented characters into base + diacritics
    .replace(/[\u0300-\u036f]/g, '') // remove diacritics
    .replace(/[\u200B-\u200D\uFEFF]/g, '') // remove zero-width spaces and other invisible characters
    .replace(/[\s\-_.,;:/\\|()[\]{}<>!?'"`~@#$%^&*+=]+/g, ''); // remove spaces and separators
};

export const matchesSearchTerms = (
  searchTerm: string, 
  ...fields: (string | null | undefined | number | boolean)[]
): boolean => {
  // Normalize and split search term
  const normalizedSearch = normalizeText(searchTerm.trim());
  
  // If no search term, return true (show all)
  if (normalizedSearch.length === 0) return true;
  
  // Split into multiple search terms if user types spaces
  const searchTerms = searchTerm
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(term => term.length > 0)
    .map(term => normalizeText(term));
  
  // Combine all fields into one searchable string
  const searchableText = fields
    .filter(field => field != null)
    .map(field => String(field).toLowerCase())
    .join(' ');
  
  const normalizedSearchableText = normalizeText(searchableText);
  
  // All search terms must be found in the text (AND logic between terms)
  return searchTerms.every(term => normalizedSearchableText.includes(term));
};

/**
 * Exact match search - searches for the complete search term as a contiguous string
 * Unlike matchesSearchTerms, this does NOT split the search term into multiple parts
 * 
 * @param searchTerm - The exact search string to find
 * @param fields - The fields to search in
 * @returns true if the exact search term is found as a contiguous string in any of the fields
 * 
 * Example:
 * matchesExactSearch("hirsch ga", customer.name, customer.email)
 * Will only find results where "hirsch ga" appears exactly (with normalization)
 */
export const matchesExactSearch = (
  searchTerm: string, 
  ...fields: (string | null | undefined | number | boolean)[]
): boolean => {
  const normalizedSearch = normalizeText(searchTerm.trim());
  
  // If no search term, return true (show all)
  if (normalizedSearch.length === 0) return true;
  
  // Combine all fields into one searchable string
  const searchableText = fields
    .filter(field => field != null)
    .map(field => String(field).toLowerCase())
    .join(' ');
  
  const normalizedSearchableText = normalizeText(searchableText);
  
  // Exact match: The normalized search must appear as a contiguous string
  return normalizedSearchableText.includes(normalizedSearch);
};
